import { type Color, Stack, Text, type TextProps } from '@stitch-fix/mode-react';
import { A, G } from '@mobily/ts-belt';
import React from 'react';
import { Link } from '@stitch-fix/sf-next';
import { isNonEmptyString } from '../../utils/helpers/isNonEmptyString';
import { useFrontDoorOffer } from '../../hooks/useFrontDoorOffer';
import type { ChildBlock, ChildFragment, ChildLink, ChildListItem, ChildSpan, ChildText, ContentStackJSONRichText } from '../../../scripts/schemaGenerator/fields/rte';
import styles from './styles.module.scss';
type JsonRteRendererOptions = {
  textProps: { [T in ElementTag]?: TextProps<T> };
  textSizeMap?: 'display' | 'title';
  color?: Color;
  /**
   * When true, skips block-level wrapper elements (h1-h6, p, li) and renders only inline content.
   * Useful for embedding RTE content within custom semantic structures.
   */
  renderAsInline?: boolean;
};
export type JsonRteRendererProps = {
  data: ContentStackJSONRichText;
  options?: JsonRteRendererOptions;
  enableOfferOverride?: boolean;
};
const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
const textAlignments = ['left', 'center', 'right', 'justify'] as const;
const validClassNames = ['uppercase'] as const;
const elementTags = [...headingTags, 'p', 'li'] as const;
type HeadingTag = (typeof headingTags)[number];
type TextAlignment = (typeof textAlignments)[number];
type ElementTag = (typeof elementTags)[number];
const isHeadingTag = (item: string): item is HeadingTag => A.includes(headingTags, item);
const isTextAlignment = (item: string): item is TextAlignment => A.includes(textAlignments, item);

// Helps pick a stable React key when a node includes a uid (Contentstack blocks) instead of falling back to index.
const hasUid = (node: RichTextNode): node is RichTextNode & {
  uid: string;
} => 'uid' in node && typeof (node as {
  uid?: unknown;
}).uid === 'string';
const isParagraphTag = (item: string): item is 'p' => item === 'p';
const isSpanTag = (item: string): item is 'span' => item === 'span';
const isOrderedListTag = (item: string): item is 'ol' => item === 'ol';
const isUnorderedListTag = (item: string): item is 'ul' => item === 'ul';
const isListItemTag = (item: string): item is 'li' => item === 'li';
const isFragmentTag = (item: string): item is 'fragment' => item === 'fragment';
const isTextElement = (element: unknown): element is ChildText => typeof element === 'object' && element !== null && 'text' in element;
const isValidClassName = (className?: string) => G.isString(className) && A.includes(validClassNames, className.trim());
type RichTextNode = ChildBlock | ChildLink | ChildText | ChildSpan | ChildListItem | ChildFragment;
type RichTextParentNode = Exclude<RichTextNode, ChildText>;
type HeadingMergeChild = ChildBlock['children'][number] & {
  headingMerge?: boolean;
};
const isHeadingMergeText = (child: ChildText): child is ChildText & {
  headingMerge: true;
} => 'headingMerge' in child && child.headingMerge === true;
const hasChildren = (node: RichTextNode): node is RichTextParentNode => 'children' in node && Array.isArray(node.children);
const isHeadingBlock = (node: RichTextNode | undefined): node is ChildBlock & {
  type: HeadingTag;
} => Boolean(node && 'type' in node && isHeadingTag(node.type));

// Recursively normalize nested children to RichTextNode so downstream logic can treat everything uniformly.
const normalizeNode = (node: RichTextNode): RichTextNode => {
  if (hasChildren(node)) {
    return {
      ...node,
      children: normalizeChildren(node.children as unknown as RichTextNode[])
    } as RichTextNode;
  }
  return node;
};

// Collapses adjacent headings of the same level into one node,
// inserting a line break to preserve visual separation. https://stitchfix.atlassian.net/browse/ONB-2262
const mergeConsecutiveHeadings = (children: RichTextNode[]) => {
  return children.reduce<RichTextNode[]>((acc, child) => {
    const last = acc[acc.length - 1];
    if (isHeadingBlock(last) && isHeadingBlock(child) && last.type === child.type) {
      const headingBreak = {
        text: '',
        break: true,
        headingMerge: true
      } satisfies HeadingMergeChild;
      const mergedChildren = [...last.children, headingBreak, ...child.children] satisfies HeadingMergeChild[];
      acc[acc.length - 1] = {
        ...last,
        children: mergedChildren
      };
      return acc;
    }
    acc.push(child);
    return acc;
  }, []);
};
const normalizeChildren = (children?: RichTextNode[]): RichTextNode[] => {
  if (!Array.isArray(children)) {
    return [];
  }
  return mergeConsecutiveHeadings(children.map(normalizeNode));
};
export const isEmpty = (data: RichTextNode[] | undefined): boolean => {
  if (!Array.isArray(data)) {
    return true;
  }
  return data.every(child => {
    if ('children' in child && Array.isArray(child.children)) {
      return isEmpty(child.children);
    }
    return isTextElement(child) && !isNonEmptyString(child.text);
  });
};
const getTextStyle = (child: RichTextNode) => {
  let style: React.CSSProperties | undefined;
  if ('attrs' in child && 'class-name' in child.attrs && isValidClassName(child.attrs['class-name']) && child.attrs['class-name'] === 'uppercase') {
    style = {
      textTransform: 'uppercase'
    };
  }
  return style;
};
const getTextAlignment = (child: RichTextNode) => {
  let textAlignment: TextAlignment = 'left';
  if ('attrs' in child && 'style' in child.attrs && child.attrs.style !== undefined && 'text-align' in child.attrs.style && isTextAlignment(child.attrs.style['text-align'])) {
    textAlignment = child.attrs.style['text-align'];
  }
  return textAlignment;
};
const TEXT_TYPE_SIZE_MAP: Record<ElementTag, TextProps['setting']> = {
  h1: 'title-xlarge',
  h2: 'title-large',
  h3: 'title-medium',
  h4: 'title-medium',
  h5: 'title-xsmall',
  h6: 'body-large',
  p: 'body-medium',
  li: 'body-medium'
};
const DISPLAY_TEXT_TYPE_SIZE_MAP: Record<ElementTag, TextProps['setting']> = {
  h1: 'display-xlarge',
  h2: 'display-large',
  h3: 'display-medium',
  h4: 'title-medium',
  h5: 'title-xsmall',
  h6: 'body-large',
  p: 'body-medium',
  li: 'body-medium'
};
const getDefaultOptions = (): JsonRteRendererOptions => {
  const defaults = {
    spacingBottom: true,
    color: 'gray-16' as const
  };
  const headings = headingTags.reduce((acc, heading) => {
    return {
      ...acc,
      [heading]: {
        ...defaults
      }
    };
  }, {});
  return {
    textSizeMap: 'title',
    textProps: {
      ...headings,
      p: {
        ...defaults
      },
      li: {
        ...defaults
      }
    }
  };
};
const mergeDefaultOptions = (options: JsonRteRendererOptions, textProp: keyof JsonRteRendererOptions['textProps']) => {
  const defaults = getDefaultOptions();
  return {
    ...defaults.textProps[textProp],
    color: options.color,
    ...options.textProps[textProp]
  };
};
type MergedTextOptions = ReturnType<typeof mergeDefaultOptions>;

/** When any descendant uses Mode Text settings, the outer Text should not inherit default spacing/color. */
const hasDescendantWithModeTextSetting = (node: RichTextNode): boolean => {
  if (isTextElement(node)) {
    return Boolean(node.modeTextSetting);
  }
  if (hasChildren(node)) {
    return node.children.some(hasDescendantWithModeTextSetting);
  }
  return false;
};
export const JsonRteRenderer = ({
  data,
  options = getDefaultOptions(),
  enableOfferOverride = false
}: JsonRteRendererProps) => {
  const {
    offer
  } = useFrontDoorOffer(!enableOfferOverride);
  const shouldOverride = enableOfferOverride && offer && offer.cell !== 'fixed';
  const renderText = (child: unknown) => {
    if (isTextElement(child)) {
      let displayText = child.text;
      if (shouldOverride) {
        if (offer?.cell === 'percentage') {
          displayText = displayText.replace('$20 off', `${offer.discount.percentage}% off`);
        } else if (offer?.cell === 'no-promo') {
          displayText = displayText.replace(/\$20 off\s*/g, '');
        }
      }
      let node: React.ReactNode = React.createElement(React.Fragment, undefined, displayText);
      if (child.bold) {
        node = React.createElement('strong', undefined, node);
      }
      if (child.italic) {
        node = React.createElement('em', undefined, node);
      }
      if (child.strikethrough) {
        node = React.createElement('s', undefined, node);
      }
      if (child.break) {
        // Only add spacing for heading merges, not regular breaks
        if (isHeadingMergeText(child)) {
          node = React.createElement(React.Fragment, undefined, node, React.createElement('span', {
            className: styles.rteHeadingBreak,
            'aria-hidden': 'true'
          }));
        } else {
          // Regular break from ContentStack data
          node = React.createElement(React.Fragment, undefined, node, React.createElement('br'));
        }
      }
      if (child.classname) {
        const classNames = child.classname.split(' ');
        if (classNames.includes('uppercase')) {
          node = React.createElement('span', {
            style: {
              textTransform: 'uppercase'
            }
          }, node);
        }
      }
      const modeTextSetting = child.modeTextSetting;
      if (modeTextSetting) {
        node = <Text as="span" setting={modeTextSetting} spacingBottom={false}>
            {node}
          </Text>;
      }
      return node;
    }
    return '';
  };
  const parseElementChildren = (child: RichTextParentNode, parser: typeof renderItems | typeof renderText) => {
    return child.children.map((el, index) =>
    // List is static and won't change once rendered
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={`${child.uid}-${index}`}>
        {parser(el)}
      </React.Fragment>);
  };
  const applyClassnameOverrides = (child: RichTextNode) => {
    const includesDisclaimerText = 'type' in child && isParagraphTag(child.type) && child.children.some(c => 'classname' in c && c.classname === 'disclaimer');
    if (includesDisclaimerText) {
      return {
        setting: 'body-small' as const
      };
    }
    return undefined;
  };
  const renderItems = (child: RichTextNode) => {
    if ('type' in child && child.type === 'a') {
      return <Link href={child.attrs.url} target={child.attrs.target}>
          {parseElementChildren(child, renderItems)}
        </Link>;
    }
    if ('type' in child && (isHeadingTag(child.type) || isParagraphTag(child.type) || isListItemTag(child.type))) {
      const asComponent = isListItemTag(child.type) ? undefined : child.type;

      // If renderAsInline, skip the Text wrapper and just render children
      if (options.renderAsInline) {
        return <React.Fragment key={hasUid(child) ? child.uid : undefined}>
            {parseElementChildren(child, renderText)}
          </React.Fragment>;
      }
      const blockHasModeTextDescendant = hasDescendantWithModeTextSetting(child);
      let textPropsForBlock: MergedTextOptions | Omit<MergedTextOptions, 'setting'>;
      if (blockHasModeTextDescendant) {
        const mergedTextProps = mergeDefaultOptions(options, child.type);
        // Omit block `setting` when descendants use mode text (union members may not include `setting`).
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit `setting` from spread
        const {
          setting,
          ...rest
        } = mergedTextProps as MergedTextOptions & {
          setting?: TextProps['setting'];
        };
        textPropsForBlock = rest;
      } else {
        textPropsForBlock = mergeDefaultOptions(options, child.type);
      }
      const textTypeSizeMap = options.textSizeMap === 'display' ? DISPLAY_TEXT_TYPE_SIZE_MAP : TEXT_TYPE_SIZE_MAP;
      const blockSetting = blockHasModeTextDescendant ? undefined : textTypeSizeMap[child.type];
      return <Text as={asComponent} align={getTextAlignment(child)} style={getTextStyle(child)} setting={blockSetting} {...textPropsForBlock} {...applyClassnameOverrides(child)}>
          {parseElementChildren(child, renderItems)}
        </Text>;
    }
    if ('type' in child && isSpanTag(child.type)) {
      return parseElementChildren(child, renderText);
    }
    if ('type' in child && isFragmentTag(child.type)) {
      return <React.Fragment key={hasUid(child) ? child.uid : undefined}>
          {parseElementChildren(child, renderItems)}
        </React.Fragment>;
    }
    if ('type' in child && (isOrderedListTag(child.type) || isUnorderedListTag(child.type))) {
      return <Stack as={child.type} spacing={0} mt={0.5}>
          {parseElementChildren(child, renderItems)}
        </Stack>;
    }

    // if we reached here, we have a text element and don't need to recurse
    return renderText(child);
  };
  return <>
      {normalizeChildren(data?.children).map((child, index) => <React.Fragment key={hasUid(child) ? child.uid : `rte-${index}`}>
          {renderItems(child)}
        </React.Fragment>)}
    </>;
};